import { components, paths } from "@/api-schema";
import {
  PathsV1JobFunctionSearchPostParametersQueryOrderByDirection,
  PathsV1JobFunctionSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useJobFunctionDeleteMutation } from "@/common/api/job-function/use-job-function-delete.mutation";
import { useJobFunctionRestoreMutation } from "@/common/api/job-function/use-job-function-restore.mutation";
import { useJobFunctionSearchQuery } from "@/common/api/job-function/use-job-function-search.query";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { TableFeatures } from "@/components/data-table/types";
import { DeletedStatus, DeletedStatusFilter } from "@/components/filter/predefined/deleted-status-filter";
import i18next from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { jobFunctionTableDefaultColumns } from "./job-function-table-columns";

type JobFunction = components["schemas"]["JobFunction"] & RowWithIdentifier;
type JobFunctionSorting = NonNullable<paths["/v1/job-function/search"]["post"]["parameters"]["query"]["orderBy"]>;
type JobFunctionQueryParams = paths["/v1/job-function/search"]["post"]["parameters"]["query"];
type JobFunctionRequestBody = paths["/v1/job-function/search"]["post"]["requestBody"]["content"]["application/json"];

const DEFAULT_COLUMNS = [
  jobFunctionTableDefaultColumns.columnName,
  jobFunctionTableDefaultColumns.columnTechnicalId,
  jobFunctionTableDefaultColumns.columnDeletedAt,
];

const JOB_FUNCTION_TABLE_CONFIG: PredefinedTableConfig<JobFunctionSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1JobFunctionSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1JobFunctionSearchPostParametersQueryOrderByField.Name,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: i18next.t("common:SearchJobFunctions"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: true,
    showDelete: true,
    showView: true,
  } as const,
  sortingMapping: {
    name: ["name"],
    technicalId: ["technicalId"],
    v1Id: ["v1Id"],
    deletedAt: ["deletedAt"],
    createdAt: ["createdAt"],
    updatedAt: ["updatedAt"],
  } as const,
} as const;

const JOB_FUNCTION_TABLE_QUERY_HOOKS: DataFetchHooks<JobFunction, JobFunctionQueryParams, JobFunctionRequestBody> = {
  useSearchQuery: useJobFunctionSearchQuery,
  useDeleteMutation: useJobFunctionDeleteMutation,
  useRestoreMutation: useJobFunctionRestoreMutation,
} as const;

export interface JobFunctionTableProps extends BasePredefinedTableProps<
  JobFunction,
  JobFunctionSorting,
  JobFunctionQueryParams,
  JobFunctionRequestBody
> {}

const BaseJobFunctionTable = createBasePredefinedTable({
  config: JOB_FUNCTION_TABLE_CONFIG,
  queryHooks: JOB_FUNCTION_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function JobFunctionTable(props: JobFunctionTableProps) {
  const { t } = useTranslation(["common"]);
  const {
    name = "job-function-table",
    showCreateButton = true,
    body = { deletedAt: [DeletedStatus.ACTIVE, DeletedStatus.DELETED] },
    filters,
    filterValues,
    getRowCanExpand,
    columns,
    initialSorting = [
      {
        direction: PathsV1JobFunctionSearchPostParametersQueryOrderByDirection.Asc,
        field: PathsV1JobFunctionSearchPostParametersQueryOrderByField.Name,
      },
    ],
    ...restProps
  } = props;

  const defaultFilters = useMemo(() => filters ?? [new DeletedStatusFilter()], [filters]);
  const defaultFilterValues = useMemo(() => filterValues ?? { deletedAt: [DeletedStatus.ACTIVE] }, [filterValues]);

  return (
    <BaseJobFunctionTable
      searchPlaceholder={t("common:SearchJobFunctions")}
      {...{
        name,
        showCreateButton,
        body,
        columns,
        filters: defaultFilters,
        filterValues: defaultFilterValues,
        getRowCanExpand,
        initialSorting,
        confirmActions: ["delete", "restore"],
        toastMessages: {
          deleteSuccess: t("JobFunctionHasBeenDeleted"),
          deleteError: t("DeleteJobFunctionError"),
          restoreSuccess: t("JobFunctionHasBeenRestored"),
          restoreError: t("RestoreJobFunctionError"),
        },
        confirmLabels: {
          delete: (row: JobFunction) => ({
            title: row.deletedAt ? t("DeleteJobFunctionPermanently") : t("DeleteJobFunction"),
            text: row.deletedAt ? t("DeleteJobFunctionPermanentlyDescription") : t("DeleteJobFunctionDescription"),
            confirmButton: row.deletedAt ? t("YesDeletePermanently") : t("ram:YesDelete"),
            cancelButton: t("Cancel"),
          }),
          restore: {
            title: t("RestoreJobFunction"),
            text: t("RestoreJobFunctionDescription"),
            confirmButton: t("YesRestore"),
            cancelButton: t("Cancel"),
          },
        },
        ...restProps,
      }}
    />
  );
}
